import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { HashLink } from 'react-router-hash-link';
import styles from "./Contact.module.css";
import homeStyles from "./Home.module.css";
import { API_BASE_URL } from '../config/api.js';


// --- Custom Hook for animations ---
function useOnScreen(options) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.unobserve(entry.target);
      }
    }, options);

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [ref, options]);

  return [ref, isVisible];
}

// --- FAQ Item Component ---
function FaqItem({ q, a }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div 
      className={styles.faqItem} 
      onClick={() => setIsOpen(!isOpen)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setIsOpen(!isOpen);
        }
      }}
      aria-expanded={isOpen}
    >
      <div className={styles.faqQ}>
        <span>{q}</span>
        <span className={`${styles.faqIcon} ${isOpen ? styles.faqIconOpen : ''}`}>+</span>
      </div>
      {isOpen && <div className={styles.faqA}>{a}</div>}
    </div>
  );
}

export default function Contact() {
  const [menuOpen, setMenuOpen] = useState(false);
  const handleMenuClose = () => setMenuOpen(false);
  
  // Form state
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [formSent, setFormSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // --- Refs for animations ---
  const [testimonialsRef, isTestimonialsVisible] = useOnScreen({ threshold: 0.2 });
  const [howItWorksRef, isHowItWorksVisible] = useOnScreen({ threshold: 0.2 });
  const [formRef, isFormVisible] = useOnScreen({ threshold: 0.2 });
  const [faqRef, isFaqVisible] = useOnScreen({ threshold: 0.2 });

  // Data using provided images
  const testimonials = [
    { name: "Alicja Bukowska, Biuro Rachunkowe", text: "Z Paperly oszczędzamy ponad 10 godzin tygodniowo na obsłudze umów. To realna zmiana, która odciążyła zespół i przyspieszyła pracę. Z mojej strony mogę gorąco polecić !", img: "opinion1.png", stars: 5 },
    { name: "Marek Kowalski, Kancelaria Prawna", text: "Dzięki automatyzacji dokumentów odciążyliśmy zespół, przyspieszyliśmy obsługę i podnieśliśmy jakość pracy. Klienci zauważyli różnicę, a my w końcu możemy skupić się na tym, co naprawdę ważne.", img: "/opinion2.png", stars: 5 },
  ];
  const howItWorksSteps = [
    { icon: "1", title: "Umów konsultację", desc: "Podczas 30-minutowej rozmowy poznamy Twoje potrzeby." },
    { icon: "2", title: "Otrzymaj dostęp", desc: "Po rozmowie otrzymasz darmowy, 14 dniowy dostęp do Paperly." },
    { icon: "3", title: "Automatyzuj!", desc: "Generuj setki dokumentów za pomocą jednego kliknięcia." },
  ];

  async function handleSubmit(e) {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          message: message
        })
      });

      if (response.ok) {
        setFormSent(true);
        setEmail("");
        setMessage("");
        setTimeout(() => setFormSent(false), 4000);
      } else {
        throw new Error('Błąd wysyłania wiadomości');
      }
    } catch (err) {
      setError("Nie udało się wysłać wiadomości. Spróbuj ponownie.");
      setTimeout(() => setError(""), 4000);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className={styles.pageWrapper}>
      {/* HEADER (navbar) */}
      <header className={homeStyles.header}>
        <Link to="/">
          <img src="/logo2.png" className={homeStyles.logo} alt="Paperly logo" />
        </Link>
        <nav className={homeStyles.navBar}>
          <div className={homeStyles.navLinks}></div>
          <div className={homeStyles.navButtons}>
            <HashLink smooth to="#formularz" className={homeStyles.navBtnPrimary}>Bezpłatna konsultacja</HashLink>
            <Link to="/login" className={homeStyles.navBtnSecondary}>Zaloguj</Link>
          </div>
        </nav>
        <button className={homeStyles.hamburger} aria-label="Otwórz menu" onClick={() => setMenuOpen(true)}>
          <span /> <span /> <span />
        </button>
        {menuOpen && (
          <div className={homeStyles.menuOverlay} onClick={handleMenuClose}>
            <nav className={homeStyles.mobileMenu} onClick={e => e.stopPropagation()}>
              <button className={homeStyles.closeMenu} aria-label="Zamknij menu" onClick={handleMenuClose}>&times;</button>
              <HashLink smooth to="#formularz" className={homeStyles.ctaBtn} onClick={handleMenuClose}>Bezpłatna konsultacja</HashLink>
            </nav>
          </div>
        )}
      </header>

      <main className={styles.mainContent}>
        <div className={styles.contentWrapper}>
          {/* HERO SECTION */}
          <section className={styles.heroSection}>
            <div className={styles.heroContent}>
              <h1 className={styles.heroTitle}>
              Zmień swoje biuro już dziś!
                <br />
              </h1>
              <p className={styles.heroSubtitle}>
                Nasz ekspert pokaże Ci, jak Paperly może zautomatyzować Twoją pracę z dokumentami. Umów bezpłatną 30-minutową konsultację i odbierz dostęp testowy.
              </p>
              <HashLink smooth to="#formularz" className={styles.heroCta}>🚀 Umów bezpłatną konsultację</HashLink>
              <div className={styles.trustRow}>
                <span className={styles.trustItem}><span className={styles.badgeDot}></span> 10 000+ wygenerowanych dokumentów</span>
                <span className={styles.trustItem}><span className={styles.badgeDot}></span> Wsparcie 1:1</span>
              </div>
            </div>
          </section>
          {/* HOW IT WORKS */}
          <section ref={howItWorksRef} className={`${styles.howItWorksSection} ${isHowItWorksVisible ? styles.fadeInUp : ''}`}>
            <div className={styles.howItWorksContent}>
              <div className={styles.servicesTitle}>Jak to działa?</div>
              <h2 className={styles.sectionTitle}>Twoja droga do automatyzacji w 3 krokach</h2>
              <div className={styles.howItWorksGrid}>
                {howItWorksSteps.map((s, i) => (
                  <div className={styles.howItWorksStep} key={i}>
                    <div className={styles.howItWorksIcon}>{s.icon}</div>
                    <h3 className={styles.howItWorksTitle}>{s.title}</h3>
                    <p className={styles.howItWorksDesc}>{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FORM */}
          <section ref={formRef} className={`${styles.formSection} ${isFormVisible ? styles.fadeInUp : ''}`} id="formularz">
            <div className={styles.formSectionContent}>
              <div className={styles.formContainer}>
                <div className={styles.servicesTitle}>Skontaktuj się z nami</div>
                <h2 className={styles.sectionTitle}>Gotowy na oszczędność czasu?<br/>Porozmawiajmy!</h2>
              <form className={styles.contactForm} onSubmit={handleSubmit}>
                <input 
                  className={styles.contactInput} 
                  type="email" 
                  placeholder="Twój adres e-mail*" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required 
                  disabled={isLoading}
                />
                <textarea 
                  className={styles.contactInput} 
                  placeholder="Czego chcesz się dowiedzieć? (np. Automatyzacja umów)" 
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required 
                  disabled={isLoading}
                  rows={4}
                />
                {/* Honeypot for spam bots */}
                <input type="text" name="company_website" className={styles.visuallyHidden} tabIndex={-1} autoComplete="off" aria-hidden="true" />
                <button 
                  type="submit" 
                  className={styles.contactBtnBlue}
                  disabled={isLoading}
                >
                  {isLoading ? "Wysyłanie..." : "Umów bezpłatną konsultację"}
                </button>
                <div className={styles.formNote}>Odpowiadamy w ciągu godziny.</div>
                <div className={styles.privacyNote}>Twoje dane są bezpieczne. Nie udostępniamy ich osobom trzecim.</div>
                {formSent && (
                  <div className={styles.formSuccess}>
                    <span className={styles.successIcon}>✓</span> 
                    <span>Dziękujemy! Skontaktujemy się wkrótce.</span>
                  </div>
                )}
                {error && (
                  <div className={styles.formError}>
                    <span className={styles.errorIcon}>✗</span> 
                    <span>{error}</span>
                  </div>
                )}
              </form>
              </div>
            </div>
          </section>
          {/* Sticky CTA for mobile removed to avoid overlay on phones */}
          
          {/* FAQ */}
          <section ref={faqRef} className={`${styles.faqSection} ${isFaqVisible ? styles.fadeInUp : ''}`}>
            <div className={styles.faqSectionContent}>
              <div className={styles.servicesTitle}>Masz pytania?</div>
              <h2 className={styles.sectionTitle}>Najczęstsze pytania</h2>
              <div className={styles.faqList}>
                <FaqItem q="Jak mogę przetestować Paperly za darmo?" a="Dostęp do darmowej wersji testowej przyznajemy po krótkiej, 30-minutowej konsultacji, aby upewnić się, że w pełni wykorzystasz potencjał aplikacji." />
                <FaqItem q="Czy Paperly integruje się z innymi systemami?" a="Tak! Standardowo Paperly współpracuje z plikami Excel (.xlsx), co pozwala na masowe generowanie dokumentów. Dodatkowo, oferujemy możliwość integracji z innymi systemami (np. CRM, ERP, API) w ramach indywidualnego wdrożenia. Opowiedz nam o swoich narzędziach podczas rozmowy." />
                <FaqItem q="Jak długo trwa wdrożenie?" a="Pierwsze efekty zobaczysz już po kilku godzinach od startu." />
                <FaqItem q="Co jeśli będę potrzebować pomocy po wdrożeniu?" a="Zawsze możesz na nas liczyć. Każdy klient otrzymuje dostęp do wsparcia mailowego i telefonicznego. Osobiście pomagamy w tworzeniu nowych szablonów i optymalizacji procesów, abyś mógł w pełni wykorzystać potencjał Paperly." />
                <FaqItem q="Czy moje dane są bezpieczne?" a="Tak, Paperly spełnia wszystkie wymogi RODO i dba o bezpieczeństwo Twoich dokumentów." />
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* FOOTER */}
      <footer className={homeStyles.footer}>
        <div className={homeStyles.footerContent}>
          <div className={homeStyles.footerBrand}>
            <img src="/logo2.png" alt="Paperly Logo" className={homeStyles.footerLogo} />
            <div className={homeStyles.footerBrandText}>
              <span>&copy; {new Date().getFullYear()} Paperly. Wszelkie prawa zastrzeżone.</span>
              <span className={homeStyles.madeInPoland}>Stworzone z ❤️ w Polsce</span>
            </div>
          </div>
          <div className={homeStyles.footerLinks}>
            <Link to="/" className={homeStyles.footerLink}>Strona główna</Link>
            <Link to="/polityka-prywatnosci" className={homeStyles.footerLink}>Polityka Prywatności</Link>
            <Link to="/regulamin" className={homeStyles.footerLink}>Regulamin</Link>
          </div>
        </div>
      </footer>
    </div>
  );
} 